import useReceiptsList from '@hooks/query/e-prescriptions/useReceiptsList';
import { measureElement, useVirtualizer, Virtualizer } from '@tanstack/react-virtual';
import { Empty, LifeIdDrawer, Loader } from '@ui';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import ReceiptsListItem from './receipts-list-item';

const PARAMS = { direction: 'DESC', sortBy: 'createdAt' } as const;

interface ReceiptsListProps {
  visible?: boolean;
  clientId: string;
  onClose: () => void;
}

const ReceiptsList: React.FunctionComponent<ReceiptsListProps> = ({
  clientId,
  visible,
  onClose,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const parentRef = React.useRef<HTMLDivElement>(null);
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } = useReceiptsList(
    clientId,
    PARAMS,
    {
      enabled: !!clientId && visible,
    },
  );

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? data.length + 1 : data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 80,
    overscan: 5,
    // Custom measure to include margins
    measureElement: React.useCallback((el: any, inst: Virtualizer<HTMLDivElement, any>) => {
      return measureElement(el, inst) + 16;
    }, []),
  });

  const items = rowVirtualizer.getVirtualItems();

  React.useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= data.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, data.length, isFetchingNextPage, items]);

  return (
    <LifeIdDrawer
      visible={visible}
      ref={parentRef}
      isInfiniteScroll
      onClose={onClose}
      title={formatMessage({ id: 'med_records.receipts' })}
      contentContainerStyle={{ backgroundColor: theme.color.surface.background }}
    >
      {data.length === 0 ? (
        <Empty title={<FormattedMessage id="med_records.no_records" />} style={{ margin: 32 }} />
      ) : (
        <LifeIdDrawer.Scroller
          // Height + margin of container
          totalHeight={rowVirtualizer.getTotalSize() + 16}
          startPoint={items[0].start}
        >
          {items.map((virtualRow) => {
            const isLoaderRow = virtualRow.index > data.length - 1;
            const item = data[virtualRow.index];
            const extra = {
              key: virtualRow.key,
              'data-index': virtualRow.index,
              ref: rowVirtualizer.measureElement,
            };

            return (
              <>
                {isLoaderRow ? (
                  hasNextPage ? (
                    <div className="load-more" {...extra}>
                      <Loader size="small" />
                    </div>
                  ) : (
                    <div className="load-more" {...extra}>
                      Načítať viac
                    </div>
                  )
                ) : (
                  <ReceiptsListItem {...extra} item={item} />
                )}
              </>
            );
          })}
        </LifeIdDrawer.Scroller>
      )}
    </LifeIdDrawer>
  );
};

export default React.memo(ReceiptsList);
